import React, { useState, useRef, useEffect, useId, useCallback } from 'react';
import { createPortal } from 'react-dom';

interface TooltipProps {
  /** Text displayed inside the tooltip bubble. When empty, the tooltip is not rendered. */
  content: string;
  children: React.ReactNode;
  /** Whether the tooltip appears above or below the trigger element. */
  position?: 'top' | 'bottom';
}

/**
 * Accessible tooltip that displays contextual text on hover or keyboard focus.
 *
 * The bubble is rendered into `document.body` via `createPortal` and
 * positioned with `position: fixed` in viewport coordinates. This lets the
 * tooltip escape any ancestor with `overflow-hidden` / `overflow-y-auto`
 * (e.g. scrollable tables, clipped cards) that would otherwise obscure it.
 *
 * Renders as a passthrough wrapper when `content` is empty, so callers
 * do not need conditional logic at the call-site.
 */
export const Tooltip: React.FC<TooltipProps> = ({ content, children, position = 'top' }) => {
  const [visible, setVisible] = useState(false);
  const [coords, setCoords] = useState<{ top: number; left: number } | null>(null);
  const triggerRef = useRef<HTMLDivElement>(null);
  const timeoutRef = useRef<ReturnType<typeof setTimeout>>(undefined);
  const tooltipId = useId();

  /** Measure the trigger and set the bubble's fixed-position anchor point. */
  const computePosition = useCallback(() => {
    if (!triggerRef.current) return;
    const rect = triggerRef.current.getBoundingClientRect();
    setCoords({
      // Horizontal centre of the trigger - the bubble pairs this with a
      // `translate(-50%, ...)` so it stays centred over the element.
      left: rect.left + rect.width / 2,
      // For `top` tooltips we anchor at the trigger's top edge; for
      // `bottom`, at its bottom edge.
      top: position === 'top' ? rect.top : rect.bottom,
    });
  }, [position]);

  const show = () => {
    // Hover and focus can both open it; cancel the pending timer so none is orphaned.
    if (timeoutRef.current) clearTimeout(timeoutRef.current);
    timeoutRef.current = setTimeout(() => {
      computePosition();
      setVisible(true);
    }, 300);
  };

  const hide = () => {
    if (timeoutRef.current) clearTimeout(timeoutRef.current);
    setVisible(false);
  };

  useEffect(() => {
    return () => {
      if (timeoutRef.current) clearTimeout(timeoutRef.current);
    };
  }, []);

  // While visible, reposition on scroll (capture phase catches nested
  // scroll containers) and resize so the bubble tracks its trigger.
  useEffect(() => {
    if (!visible) return undefined;
    const handler = () => computePosition();
    window.addEventListener('scroll', handler, true);
    window.addEventListener('resize', handler);
    return () => {
      window.removeEventListener('scroll', handler, true);
      window.removeEventListener('resize', handler);
    };
  }, [visible, computePosition]);

  // When content is empty, render children without any wrapper.
  if (!content) return <>{children}</>;

  const bubbleTransform =
    position === 'top'
      // Centre horizontally; bottom of bubble sits 10px above the trigger.
      ? 'translate(-50%, calc(-100% - 10px))'
      // Centre horizontally; top of bubble sits 10px below the trigger.
      : 'translate(-50%, 10px)';

  const bubble =
    visible && coords && typeof document !== 'undefined'
      ? createPortal(
          <div
            id={tooltipId}
            role="tooltip"
            style={{
              position: 'fixed',
              top: coords.top,
              left: coords.left,
              transform: bubbleTransform,
            }}
            className="z-[9999] w-max max-w-sm px-4 py-2.5 text-sm leading-relaxed text-muted-foreground bg-background border border-border rounded-lg pointer-events-none"
          >
            {content}
            <div
              className={`absolute w-2 h-2 bg-background rotate-45 left-1/2 -translate-x-1/2 ${
                position === 'top'
                  ? '-bottom-1 border-r border-b border-border'
                  : '-top-1 border-l border-t border-border'
              }`}
            />
          </div>,
          document.body,
        )
      : null;

  return (
    <div
      ref={triggerRef}
      className="relative"
      onMouseEnter={show}
      onMouseLeave={hide}
      onFocus={show}
      onBlur={hide}
      // Dismiss on click: acting on the trigger (often opening another layer)
      // must not leave the bubble floating, since it portals above modals.
      onClick={hide}
      aria-describedby={visible ? tooltipId : undefined}
    >
      {children}
      {bubble}
    </div>
  );
};
